<template>
  <div class="inline-password-reset-container">
    <!-- Step 1: Email sent confirmation -->
    <div v-if="step === 'email-sent'" class="email-sent-section">
      <div class="section-header mb-4">
        <h3 class="text-lg font-semibold text-gray-900">Check your email</h3>
        <p class="text-sm text-gray-600">We've sent a 6-digit code to {{ userEmail }}</p>
      </div>
      
      <form @submit.prevent="handleCodeSubmit" class="space-y-4">
        <div>
          <label class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
            Enter your 6-digit code
          </label>
          <div class="code-input-container">
            <input
              v-for="(digit, index) in codeDigits"
              :key="index"
              :ref="`digit${index}`"
              v-model="codeDigits[index]"
              @input="handleCodeInput(index)"
              @keydown="handleKeyDown($event, index)"
              @paste="handlePaste"
              type="text"
              inputmode="numeric"
              maxlength="1"
              class="code-digit"
              :class="{ 'error': codeError }"
              autocomplete="off"
            >
          </div>
          <div v-if="codeError" class="text-red-600 text-sm mt-2">
            {{ codeError }}
          </div>
        </div>
        
        <div class="flex items-center justify-between">
          <button 
            type="button"
            @click="resendCode"
            :disabled="resendDisabled"
            class="text-sm font-medium"
            :class="resendDisabled ? 'text-gray-400' : 'text-pink-600 hover:text-pink-700'"
          >
            <span v-if="resendDisabled">Resend code in {{ resendTimer }}s</span>
            <span v-else>Resend code</span>
          </button>
          
          <div v-if="remainingTime > 0" class="text-sm text-gray-500">
            Code expires in {{ formatTime(remainingTime) }}
          </div>
        </div>
        
        <div class="flex gap-3">
          <button 
            type="button"
            @click="$emit('cancel')"
            class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            :disabled="!isCodeComplete || isLoading"
            class="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!isLoading">Continue</span>
            <span v-else class="flex items-center">
              <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </span>
          </button>
        </div>
      </form>
    </div>
    
    <!-- Step 2: Reset password -->
    <div v-if="step === 'reset-password'" class="reset-password-section">
      <div class="section-header mb-4">
        <h3 class="text-lg font-semibold text-gray-900">Create new password</h3>
        <p class="text-sm text-gray-600">Enter your new password below</p>
      </div>
      
      <form @submit.prevent="handlePasswordReset" class="space-y-4">
        <div>
          <label for="new-password" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
            New Password
          </label>
          <div class="relative">
            <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
              <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
            </div>
            <input 
              id="new-password" 
              v-model="passwordForm.password" 
              type="password" 
              required 
              class="form-input block w-full placeholder-gray-400 transition-all" 
              style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
              :style="{ borderColor: focusedField === 'password' ? '#EC4899' : '#D1D5DB' }"
              @focus="focusedField = 'password'"
              @blur="focusedField = ''"
              placeholder="Enter new password"
            >
          </div>
        </div>
        
        <div>
          <label for="confirm-password" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
            Confirm Password
          </label>
          <div class="relative">
            <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
              <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
            </div>
            <input 
              id="confirm-password" 
              v-model="passwordForm.passwordConfirmation" 
              type="password" 
              required 
              class="form-input block w-full placeholder-gray-400 transition-all" 
              style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
              :style="{ borderColor: focusedField === 'password-confirm' ? '#EC4899' : '#D1D5DB' }"
              @focus="focusedField = 'password-confirm'"
              @blur="focusedField = ''"
              placeholder="Confirm new password"
            >
          </div>
        </div>
        
        <button 
          type="submit" 
          :disabled="isLoading || !passwordForm.password || !passwordForm.passwordConfirmation"
          class="w-full inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="!isLoading">Reset Password & Sign In</span>
          <span v-else class="flex items-center">
            <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Resetting password...
          </span>
        </button>
      </form>
    </div>
    
    <!-- Error Message -->
    <div v-if="errorMessage" class="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {{ errorMessage }}
    </div>
    
    <!-- Success Message -->
    <div v-if="successMessage" class="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
      {{ successMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'InlinePasswordReset',
  
  props: {
    userEmail: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      step: 'email-sent',
      isLoading: false,
      focusedField: '',
      errorMessage: '',
      successMessage: '',
      
      codeDigits: ['', '', '', '', '', ''],
      codeError: '',
      verifiedCode: '',
      
      passwordForm: {
        password: '',
        passwordConfirmation: ''
      },
      
      remainingTime: 900, // 15 minutes in seconds
      resendDisabled: true,
      resendTimer: 60,
      
      intervals: []
    };
  },
  
  computed: {
    isCodeComplete() {
      return this.codeDigits.every(digit => digit !== '');
    },
    
    fullCode() {
      return this.codeDigits.join('');
    }
  },
  
  methods: {
    handleCodeInput(index) {
      const value = this.codeDigits[index];
      
      // Clear error when user types
      this.codeError = '';
      
      // Only allow digits
      if (value && !/^\d$/.test(value)) {
        this.codeDigits[index] = '';
        return;
      }
      
      // Auto-advance to next field
      if (value && index < 5) {
        this.$nextTick(() => {
          this.$refs[`digit${index + 1}`][0].focus();
        });
      }
      
      // Auto-submit when all digits entered
      if (this.isCodeComplete) {
        this.$nextTick(() => {
          this.handleCodeSubmit();
        });
      }
    },
    
    handleKeyDown(event, index) {
      // Handle backspace
      if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
        this.$refs[`digit${index - 1}`][0].focus();
      }
      
      // Handle arrow keys
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        this.$refs[`digit${index - 1}`][0].focus();
      } else if (event.key === 'ArrowRight' && index < 5) {
        event.preventDefault();
        this.$refs[`digit${index + 1}`][0].focus();
      }
    },
    
    handlePaste(event) {
      event.preventDefault();
      const paste = event.clipboardData.getData('text');
      const digits = paste.replace(/\D/g, '').slice(0, 6).split('');
      
      digits.forEach((digit, i) => {
        if (i < 6) {
          this.codeDigits[i] = digit;
        }
      });
      
      // Focus last filled digit or first empty
      const lastFilledIndex = digits.length - 1;
      const focusIndex = Math.min(lastFilledIndex, 5);
      this.$refs[`digit${focusIndex}`][0].focus();
      
      // Auto-submit if complete
      if (this.isCodeComplete) {
        this.$nextTick(() => {
          this.handleCodeSubmit();
        });
      }
    },
    
    async handleCodeSubmit() {
      if (!this.isCodeComplete) return;
      
      this.isLoading = true;
      this.codeError = '';
      
      try {
        const response = await axios.post('/password/verify-code', {
          email: this.userEmail,
          code: this.fullCode
        });
        
        if (response.data.success) {
          this.verifiedCode = this.fullCode;
          this.remainingTime = response.data.remaining_time || 0;
          this.step = 'reset-password';
          this.$nextTick(() => {
            document.getElementById('new-password')?.focus();
          });
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.codeError = error.response.data.message || 'Invalid code';
        } else {
          this.codeError = 'An error occurred. Please try again.';
        }
        
        // Clear code on error
        this.codeDigits = ['', '', '', '', '', ''];
        this.$refs.digit0[0].focus();
      } finally {
        this.isLoading = false;
      }
    },
    
    async handlePasswordReset() {
      this.isLoading = true;
      this.errorMessage = '';
      
      if (this.passwordForm.password !== this.passwordForm.passwordConfirmation) {
        this.errorMessage = 'Passwords do not match.';
        this.isLoading = false;
        return;
      }
      
      if (this.passwordForm.password.length < 8) {
        this.errorMessage = 'Password must be at least 8 characters.';
        this.isLoading = false;
        return;
      }
      
      try {
        const response = await axios.post('/password/reset-with-code', {
          email: this.userEmail,
          code: this.verifiedCode,
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.passwordConfirmation
        });
        
        if (response.data.success) {
          this.successMessage = 'Password reset successfully! Signing you in...';
          this.$emit('auth-success', response.data.user);
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.errorMessage = error.response.data.message || 'Failed to reset password.';
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    async resendCode() {
      if (this.resendDisabled) return;
      
      try {
        const response = await axios.post('/password/code-ajax', {
          email: this.userEmail
        });
        
        if (response.data.success) {
          this.successMessage = 'A new code has been sent to your email.';
          this.remainingTime = 900; // Reset to 15 minutes
          this.startResendTimer();
          
          // Clear code inputs
          this.codeDigits = ['', '', '', '', '', ''];
          this.$refs.digit0[0].focus();
          
          // Clear success message after 3 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }
      } catch (error) {
        this.errorMessage = 'Failed to resend code. Please try again.';
      }
    },
    
    startResendTimer() {
      this.resendDisabled = true;
      this.resendTimer = 60;
      
      const interval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          this.resendDisabled = false;
          clearInterval(interval);
        }
      }, 1000);
      
      this.intervals.push(interval);
    },
    
    startExpiryTimer() {
      const interval = setInterval(() => {
        this.remainingTime--;
        if (this.remainingTime <= 0) {
          clearInterval(interval);
          this.codeError = 'Code has expired. Please request a new one.';
        }
      }, 1000);
      
      this.intervals.push(interval);
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  },
  
  mounted() {
    // Focus first code input
    this.$refs.digit0[0].focus();
    
    // Start timers
    this.startResendTimer();
    this.startExpiryTimer();
  },
  
  beforeDestroy() {
    // Clear all intervals
    this.intervals.forEach(interval => clearInterval(interval));
  }
};
</script>

<style scoped>
.code-input-container {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.code-digit {
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid #D1D5DB;
  border-radius: 0.5rem;
  transition: all 0.2s;
}

.code-digit:focus {
  outline: none;
  border-color: #EC4899;
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
}

.code-digit.error {
  border-color: #EF4444;
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.form-input:focus {
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
  outline: none;
}
</style>